<template>
  <div>
    <HpsfirProPageHeader
      title="协议管理"
      title-en="AGREEMENT"
      description="服务协议与隐私条款的正文维护；仅「已发布」的内容小程序端可读"
    />

    <HpsfirProTable
      ref="tableRef"
      :columns="columns"
      :get-data="getData"
      :on-edit="updateFun"
      :loading="loading"
      :hide-export="true"
      edit-text="编辑"
      :fixed-operate="true"
    />
  </div>
</template>

<script lang="ts" setup>

/**
 * 协议管理（hpsfir-managebase-ui 1.0.0）。
 *
 * 协议固定两条（服务协议 / 隐私条款），后台只做正文与发布状态维护：
 *   - 不提供新增与删除（类型是固定的，缺失由小程序端显示空态）；
 *   - 正文按行分段，前端按段落渲染；
 *   - 内容变更时后端自动升版本号，前端无需处理。
 */
import { ref } from 'vue';
// 注意：Hpsfir* 组件一律**不要手动 import**。
// 框架会把组件按 __name 全局注册，并且**主题会用自己的实现再注册一遍**
// （registerUiComponents → registerProComponents → registerThemeComponents，
//  同名后者覆盖前者）。手动 import 拿到的是基座/默认主题那份，
// 会绕过主题覆盖 —— 页面要用主题组件就只能靠全局注册。
// 这里只 import 非组件的东西：函数（message）、组合式函数、以及类型。
import { message, type HpsfirProTableColumn, type HpsfirProTableFilter } from 'hpsfir-managebase-ui';
import { useApi } from '@/hooks/useApi';

const api = useApi();
const loading = ref(false);

const TYPE_LABELS: Record<string, string> = {
  service: '服务协议',
  privacy: '隐私条款',
};

const columns: HpsfirProTableColumn[] = [
  {
    name: 'type',
    label: '类型',
    type: 'string',
    hideEdit: true,
    render: (scope: any) => TYPE_LABELS[scope.row?.type] || scope.row?.type,
    width: 140,
  },
  {
    name: 'title',
    label: '标题',
    type: 'string',
    rule: [{ required: true, message: '请输入标题' }],
  },
  {
    name: 'version',
    label: '版本',
    type: 'string',
    hideEdit: true,
    render: (scope: any) => `v${scope.row?.version}`,
    width: 100,
  },
  {
    name: 'status',
    label: '状态',
    type: 'select',
    options: [
      { value: 1, label: '已发布' },
      { value: 0, label: '草稿' },
    ],
    rule: [{ required: true, message: '请选择状态' }],
    // 后端读回可能是 boolean，统一用 Number 归一后再判断
    render: (scope: any) => (Number(scope.row?.status) === 1 ? '已发布' : '草稿'),
    width: 120,
  },
  {
    name: 'contentLength',
    label: '字数',
    type: 'string',
    hideEdit: true,
    render: (scope: any) => `${(scope.row?.content || '').length}`,
    width: 100,
  },
  {
    name: 'content',
    label: '正文',
    type: 'textarea',
    rows: 14,
    hideTable: true,
    placeholder: '一行一段；内容变更后版本号会自动递增',
    rule: [{ required: true, message: '请输入正文' }],
  },
  {
    name: 'updateTime',
    label: '更新时间',
    type: 'string',
    hideEdit: true,
  },
];

const getData = async(_filter: HpsfirProTableFilter) => {
  loading.value = true;
  try {
    const res: any = await api.searchTrackAdminApi.agreementListSearchTrackAdmin();
    const rows = res?.data?.data || [];
    return { total: rows.length, data: rows };
  } finally {
    loading.value = false;
  }
};

const updateFun = async(data: any) => {
  const res: any = await api.searchTrackAdminApi.agreementSaveSearchTrackAdmin({
    agreementSaveVo: {
      id: data.id,
      type: data.type,
      title: data.title,
      content: data.content,
      status: Number(data.status),
    },
  } as any);
  if (res?.data?.success) {
    message.success('保存成功');
  } else {
    throw new Error(res?.data?.msg || '保存失败');
  }
};
</script>
